import { fetchM } from './fetchM';
import { rejectionFromResponse, rejectionFor, rememberLinkID, forgetLinkID } from '../utils/crmLink';
import { LEGACY_PROVIDER, MAX_LINK_PARAMS } from '../utils/crmPrefill';

// Creating a CRM link session from a legacy arrival (MS-6621, T4.7).
//
// A legacy Salesforce link arrives as `?book_name=...&book_mobile=...` — the buyer's details in
// the URL. Until now the SPA copied them into a one-day cookie and read them back on the form, so
// they outlived the tab and sat in browser history the whole time. Now they are posted once to
// `POST /crm/links/`, which answers with an opaque id, and the id is the only thing kept.
//
// The parameters still arrive in the URL — that link is generated by Salesforce and cannot be
// changed, which is the whole reason this adapter exists — but they stop being copied anywhere the
// SPA controls, and the buyer's details are re-read from the session over an authenticated call
// instead.

// A rejection body is read the same way a failed session read is; see rejectionFromResponse, where
// the body-then-header-then-status rule and its reasoning live.
function rejectionFromBody(res) {
  const fromStatus = () => ({ rejection: rejectionFromResponse(res, null) });
  if (!res || typeof res.json !== 'function') return Promise.resolve(fromStatus());
  return res.json()
    .then(body => ({ rejection: rejectionFromResponse(res, body) }))
    .catch(fromStatus);
}

// gorilla/csrf scopes its cookie to the path that issued the token, and this POST is the SPA's
// first call on /crm/links/. book-api answers GET /crm/links/ with 204 and the token for exactly
// this reason (handlers/crm_links.go, CRMLinksToken).
function primeCSRF(dispatch) {
  return dispatch(fetchM('/crm/links/', { credentials: 'include' }))
    .then(res => (res && res.headers && res.headers.get('X-CSRF-Token')) || '')
    .catch(() => '');
}

// createCRMLinkSession posts an arrival's parameters and returns `{ session }` or `{ rejection }`.
//
// It never throws: a booking must not be blocked because a CRM adapter was unhappy. The caller
// decides what a rejection means — for the legacy path it means "carry on without a pre-fill",
// because that link is how IJM agents book every day.
export function createCRMLinkSession(provider, params) {
  return (dispatch) => {
    // Whatever this tab was pointing at belongs to the previous arrival. Dropping it first means
    // a failed exchange leaves nothing behind: otherwise buyer A's id survives buyer B's failed
    // link and B's booking can consume A's session.
    forgetLinkID();
    const names = Object.keys(params || {});
    if (names.length === 0) return Promise.resolve({ rejection: rejectionFor('not_found', '') });
    // book-api answers a larger query with `too_many_parameters`; there is no point making the
    // request, and the reason we would report is the one we already know.
    if (names.length > MAX_LINK_PARAMS) {
      return Promise.resolve({ rejection: rejectionFor('too_many_parameters', '') });
    }

    return primeCSRF(dispatch).then(token => dispatch(fetchM('/crm/links/', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Csrf-Token': token },
      body: JSON.stringify({ provider, params }),
      // Both statuses are part of this endpoint's contract rather than a wrong turn: a disabled
      // provider or an unreadable query is something to explain here, not a bounce to the generic
      // forbidden screen where the reference id is lost.
      ignoreNotFound: true,
      returnForbidden: true,
    }))
      .then((res) => {
        if (!res || !res.ok) return rejectionFromBody(res);
        return res.json().then((session) => {
          // The id is mirrored into the tab as soon as it exists, so the trip through the form —
          // and a refresh or a back on the way — keeps it. sessionStorage, so a second tab does
          // not inherit somebody else's booking.
          if (session && session.id) rememberLinkID(session.id);
          return { session };
        });
      })
      .catch((err) => {
        const res = err && err.res;
        if (!res) return { rejection: rejectionFor('unavailable', '') };
        return rejectionFromBody(res);
      }));
  };
}

// createLegacySalesforceSession is the legacy `book_*` arrival, named so the call site reads as
// what it is rather than as a provider string.
export function createLegacySalesforceSession(params) {
  return createCRMLinkSession(LEGACY_PROVIDER, params);
}

export default createCRMLinkSession;



// WEBPACK FOOTER //
// ./src/actions/crmLinkSession.js